/**
 * FoodItem — a pickup that can be carried and delivered.
 *
 * Used in the Crow vs. Squirrel puzzle. Players pick up food,
 * carry it overhead, and deliver it to a NestPlatform to feed the crow.
 *
 * In Babylon: a small sphere with physics. When picked up, physics
 * is disabled and the mesh follows the carrier offset upward.
 *
 * Canon (book p.13): each item has a `kind` — "mash" (base), "seeds"
 * (mid), or "walnut" (higher-value). The crow declares a stochastic
 * per-run preference; delivering the preferred kind double-counts
 * toward the quota. `kind` is surfaced through `getKind()` so the
 * puzzle layer can read it without poking at the food model.
 */
import { Scene } from "@babylonjs/core/scene";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { PhysicsAggregate } from "@babylonjs/core/Physics/v2/physicsAggregate";
import { PhysicsShapeType } from "@babylonjs/core/Physics/v2/IPhysicsEnginePlugin";
import { Animation } from "@babylonjs/core/Animations/animation";
import { gameEvents } from "../../core/EventBus";
import { GameEvents } from "../../config/events";
import { getAssetManager, InstantiatedModel } from "../../systems/AssetManager";
import { CAT_COLLISION_GROUP } from "../players/PlayerEntity";

/**
 * Canonical food kinds delivered in the Crow vs. Squirrel puzzle.
 * `mash` is the base crow fodder per canon p.13; `seeds` is the default
 * squirrel allocation; `walnut` is the higher-value item that the
 * stochastic preference call can demand instead.
 */
export type FoodKind = "mash" | "seeds" | "walnut";

/** Per-kind visual tint so each pickup reads distinctly at a glance. */
const FOOD_KIND_COLOR: Record<FoodKind, Color3> = {
  // Warm brown/orange — unchanged from the legacy single-kind value so
  // screenshots without the new `kind` still look identical.
  mash:   new Color3(0.70, 0.35, 0.10),
  // Greenish tan seeds.
  seeds:  new Color3(0.55, 0.55, 0.22),
  // Deeper amber for the premium walnut.
  walnut: new Color3(0.45, 0.25, 0.08),
};

export class FoodItem {
  public readonly foodKey: string;
  public readonly mesh: Mesh;

  private scene: Scene;
  private material: StandardMaterial;
  /**
   * Physics body for the pickup. Nullable because unit tests run
   * against a NullEngine without the Havok plugin registered — in
   * that case we fall through to the procedural mesh without a body
   * and the pickup/drop motion-type mutations become no-ops. Real
   * levels always have physics initialized before this constructor
   * runs.
   */
  private aggregate: PhysicsAggregate | null = null;

  private carrier: Mesh | null = null;
  private _delivered = false;
  private modelInstance: InstantiatedModel | null = null;
  private readonly _kind: FoodKind;

  /** Vertical offset above carrier when being held. */
  private readonly carryOffset = 1.2;

  constructor(scene: Scene, key: string, position: Vector3, kind: FoodKind = "mash") {
    this.scene = scene;
    this.foodKey = key;
    this._kind = kind;

    // Visual: small sphere (berry / morsel)
    this.mesh = MeshBuilder.CreateSphere(
      `food_${key}`,
      { diameter: 0.35, segments: 12 },
      scene,
    );
    this.mesh.position = position.clone();

    const tint = FOOD_KIND_COLOR[kind];
    this.material = new StandardMaterial(`food_${key}_mat`, scene);
    this.material.diffuseColor = tint;
    this.material.specularColor = tint.scale(0.28);
    // Scaled from the original mash-only emissive so the walnut and seeds
    // get matching subtle self-illumination without looking washed out.
    this.material.emissiveColor = tint.scale(0.08);
    this.mesh.material = this.material;

    // Physics: dynamic sphere. Wrapped in try/catch so tests that run
    // without a Havok plugin (NullEngine-backed unit specs) can still
    // exercise pickup/deliver logic — the motion-type mutations in
    // pickUp/drop/deliver already tolerate a missing body.
    try {
      this.aggregate = new PhysicsAggregate(
        this.mesh,
        PhysicsShapeType.SPHERE,
        { mass: 0.2, restitution: 0.3, friction: 0.5 },
        scene,
      );
      this.passThroughCats();
    } catch {
      this.aggregate = null;
    }

    // Attempt to load 3D model override (falls back to procedural mesh)
    this.loadModelOverride();
  }

  private async loadModelOverride(): Promise<void> {
    try {
      // Route through AssetManager so scenes with multiple food items
      // share a single cached template and each instance is a clone.
      const instance = await getAssetManager().instantiateSharedModel(
        this.scene,
        "/assets/models/environment/food_item.glb",
      );
      this.modelInstance = instance;

      // Parent loaded roots to the food mesh for positioning
      for (const root of instance.rootNodes) {
        if (!root.parent) {
          root.parent = this.mesh;
        }
      }

      // Hide the procedural sphere (keep for physics)
      this.mesh.isVisible = false;
    } catch {
      // Fallback: keep procedural mesh — model not available yet
    }
  }

  /** Pick up this food item (attached to carrier mesh). */
  pickUp(carrier: Mesh): void {
    if (this.carrier || this._delivered) return;

    this.carrier = carrier;

    // Emit BEFORE the physics mutation so telemetry lands even if a
    // Havok error fires later in this method (disposed body during
    // scene swap, etc.).
    gameEvents.emit(GameEvents.FOOD_PICKED_UP, {
      foodKey: this.foodKey,
      kind: this._kind,
    });

    // Fully dispose the carry-body. A prior attempt kept the body
    // alive at KINEMATIC motion type so pickup/drop could just flip
    // a flag, but that still retained the collision shape — and
    // because the per-frame position copy (`FoodItem.update()`) runs
    // AFTER the physics step, the food body sat one frame stale
    // above Basil's capsule during OvLeap. On each rise-frame the
    // capsule rammed the stale shape, transmitting a ~35×gravity
    // brake that clipped vy from 22 → 8 → <0 within three frames
    // and capped peak y at ~2 instead of the expected ~17.
    //
    // Removing the body entirely during carry eliminates the
    // interaction. `drop()` rebuilds a fresh DYNAMIC aggregate from
    // the mesh's current transform so the dropped food falls and
    // collides normally. The nest delivery check is distance-based
    // on `mesh.position`, not body presence, so delivery still
    // fires while carried.
    //
    // Bug found via QA navigator diagnostic (2026-04-18). See
    // `docs/qa/PER-PUZZLE-STATUS.md` "real gameplay bugs" entry.
    try {
      this.aggregate?.dispose();
    } catch {
      // Body already disposed / plugin unavailable — both benign.
    }
    this.aggregate = null;
  }

  /** Drop the food item at current position. */
  drop(): void {
    if (!this.carrier) return;

    this.carrier = null;

    // Rebuild a fresh DYNAMIC aggregate at the current mesh
    // transform so the food falls under gravity and collides
    // normally with ground / nest triggers. Matches the original
    // constructor config.
    try {
      this.aggregate = new PhysicsAggregate(
        this.mesh,
        PhysicsShapeType.SPHERE,
        { mass: 0.2, restitution: 0.3, friction: 0.5 },
        this.scene,
      );
      this.passThroughCats();
    } catch {
      this.aggregate = null;
    }
  }

  /**
   * Loose food rests on the ground but doesn't collide with the cats:
   * pickup is by proximity, and a walking cat used to kick the light,
   * low-friction spheres along until they rolled out of the level (the
   * crow's nest is past all three, so both cats walk through them).
   */
  private passThroughCats(): void {
    const shape = this.aggregate?.shape;
    if (!shape) return;
    shape.filterCollideMask = (shape.filterCollideMask & ~CAT_COLLISION_GROUP) >>> 0;
  }

  /**
   * Knock the item out of its carrier's paws: it reappears at `position`
   * and falls from there (the Crow vs. Squirrel squirrel snatching it out
   * of the nest and dropping it at the foot of the tree).
   */
  dropAt(position: Vector3): void {
    if (!this.carrier || this._delivered) return;
    this.mesh.position.copyFrom(position);
    this.mesh.computeWorldMatrix(true);
    this.drop();
  }

  /** Mark as delivered (e.g., to crow's nest). */
  deliver(): void {
    if (this._delivered) return;

    this._delivered = true;
    this.carrier = null;

    // Emit BEFORE any animation / physics mutation. Same rationale
    // as pickUp: telemetry + puzzle listeners need to see the
    // delivery transition even if downstream Havok / Babylon
    // Animation calls throw (e.g. during a scene dispose that
    // races with a delivery frame).
    gameEvents.emit(GameEvents.FOOD_DELIVERED, {
      foodKey: this.foodKey,
      kind: this._kind,
    });

    // Shrink and fade
    const fps = 30;
    const frames = 12;

    const scaleAnim = new Animation(
      `foodDeliver_${this.foodKey}_scale`,
      "scaling",
      fps,
      Animation.ANIMATIONTYPE_VECTOR3,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    scaleAnim.setKeys([
      { frame: 0, value: new Vector3(1, 1, 1) },
      { frame: frames, value: new Vector3(0.3, 0.3, 0.3) },
    ]);

    const visAnim = new Animation(
      `foodDeliver_${this.foodKey}_vis`,
      "visibility",
      fps,
      Animation.ANIMATIONTYPE_FLOAT,
      Animation.ANIMATIONLOOPMODE_CONSTANT,
    );
    visAnim.setKeys([
      { frame: 0, value: 1 },
      { frame: frames, value: 0 },
    ]);

    this.mesh.animations = [scaleAnim, visAnim];
    this.scene.beginAnimation(this.mesh, 0, frames, false, 1, () => {
      this.mesh.isVisible = false;
    });

    // No physics body to mutate — `pickUp()` disposed it, and delivery
    // happens mid-carry. The shrink-and-fade animation is mesh-only.
  }

  /**
   * Call each frame. Follows carrier if being held.
   */
  update(): void {
    if (this.carrier && !this._delivered) {
      this.mesh.position.x = this.carrier.position.x;
      this.mesh.position.y = this.carrier.position.y + this.carryOffset;
      this.mesh.position.z = this.carrier.position.z;
    }
  }

  get isCarried(): boolean {
    return !!this.carrier;
  }

  get isDelivered(): boolean {
    return this._delivered;
  }

  /** Canonical food kind (mash / seeds / walnut). */
  get kind(): FoodKind {
    return this._kind;
  }

  dispose(): void {
    if (this.modelInstance) {
      this.modelInstance.dispose();
      this.modelInstance = null;
    }
    this.aggregate?.dispose();
    this.aggregate = null;
    this.material.dispose();
    this.mesh.dispose();
  }
}
